import { useRef, useState, useCallback } from "react";
import { Wand2 } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

export default function BeforeAfter() {
  const frameRef = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = useCallback((clientX) => {
    const rect = frameRef.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(97, Math.max(3, p)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    frameRef.current.setPointerCapture(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => dragging.current && update(e.clientX);
  const onPointerUp = () => (dragging.current = false);

  return (
    <section id="transformation" data-testid="before-after-section" className="relative bg-ivory py-32 sm:py-44">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10">
        <FadeUp className="mb-6">
          <p className="text-[12px] tracking-[0.45em] uppercase text-coral">The Transformation</p>
        </FadeUp>
        <h2 className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-8">
          <MaskedLines inView lines={[<>From Old parquet dance floors to a <span className="italic text-coral">Fantasy Experience</span></>]} />
        </h2>
        <FadeUp delay={0.1} className="mb-14 sm:mb-16">
          <p className="text-ink/60 text-base max-w-xl">
            Drag the wand across the room and watch an ordinary venue floor become the centerpiece of the night.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div
            ref={frameRef}
            data-testid="before-after-slider"
            className="group relative w-full aspect-[4/3] sm:aspect-[16/9] rounded-[2rem] overflow-hidden select-none cursor-ew-resize shadow-[0_40px_90px_-30px_rgba(45,40,51,0.45)]"
            style={{ touchAction: "none" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <img
              data-testid="after-image"
              src="/assets/floors/after.webp"
              alt="After — Fantasy dancefloor installed"
              className="absolute inset-0 w-full h-full object-cover"
              draggable="false"
            />
            <div
              className="absolute inset-0"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            >
              <img
                data-testid="before-image"
                src="/assets/floors/before.webp"
                alt="Before — bare venue floor"
                className="absolute inset-0 w-full h-full object-cover"
                draggable="false"
              />
            </div>

            <span className="absolute top-5 left-6 px-4 py-1.5 rounded-full bg-midnight/50 backdrop-blur-md text-ivory text-[11px] tracking-[0.35em] uppercase pointer-events-none">
              Before
            </span>
            <span className="absolute top-5 right-6 px-4 py-1.5 rounded-full bg-ivory/60 backdrop-blur-md text-ink text-[11px] tracking-[0.35em] uppercase pointer-events-none">
              After
            </span>

            <div
              data-testid="before-after-handle"
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{ left: `${pos}%` }}
            >
              <div className="absolute top-0 bottom-0 -translate-x-1/2 w-px bg-ivory/90 shadow-[0_0_18px_rgba(253,251,247,0.8)]" />
              <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-ivory text-ink flex items-center justify-center shadow-[0_10px_30px_rgba(45,40,51,0.35)] transition-transform duration-300 group-active:scale-90">
                <Wand2 size={18} strokeWidth={2} className="text-coral" />
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
